import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { VideoTypesDataService, VideoTypesColumnsService } from '../../../services';
import { VideoType, VideoTypesResult } from '../../../interfaces';

@Component({
  selector: 'app-video-type-form',
  templateUrl: './video-type-form.component.html',
  styleUrls: ['./video-type-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class VideoTypeFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public videoType: VideoType = {};
  public videoTypeForm: FormGroup = new FormGroup({});
  public videoTypes: VideoType[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;


  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private videoTypesDataService: VideoTypesDataService,
    public videoTypesColumnsService: VideoTypesColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'VideoTypeFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Video Type - Edit');
        this.getVideoType(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.videoType !== 'undefined') {
      this.videoType = this.dynamicDialogConfig.data.videoType;
    }
    if (typeof this.videoType.id === 'undefined' || this.videoType.id === null) {
      this.videoType.id = null;
      this.videoType.isPublished = false;
    }
    this.getVideoTypes();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'VideoTypeFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'VideoTypeFormComponent');
  }

  getVideoType(id: number): void {
    this.isLoading = true;
    this.videoTypesDataService.getVideoType(id).subscribe((response: VideoType|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.videoType = response;
        this.prepareFrom();
      }
    });
  }

  getVideoTypes(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.videoTypes !== 'undefined' && this.dynamicDialogConfig.data.videoTypes.length > 0) {
      this.videoTypes = this.dynamicDialogConfig.data.videoTypes;
      return;
    }
    this.videoTypesDataService.getVideoTypes(this.parameters).subscribe((response: VideoTypesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.videoTypes = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.videoTypeForm = new FormGroup({
      id: new FormControl(this.videoType.id, []),
      name: new FormControl(this.videoType.name, [Validators.required, ]),
      nameAr: new FormControl(this.videoType.nameAr, []),
      nameFr: new FormControl(this.videoType.nameFr, []),
      slug: new FormControl(this.videoType.slug, [Validators.required, ]),
      slugAr: new FormControl(this.videoType.slugAr, []),
      slugFr: new FormControl(this.videoType.slugFr, []),
      extensions: new FormControl(this.videoType.extensions, [Validators.required, ]),
      mimeTypes: new FormControl(this.videoType.mimeTypes, [Validators.required, ]),
      isPublished: new FormControl(this.videoType.isPublished, [Validators.required, ]),
    });
  }

  list() {
    this.router.navigate(['/video-types']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.videoTypeForm.controls).forEach((field: any) => {
        const control = this.videoTypeForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.videoTypeForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.videoType = this.videoTypeForm.value;
    this.disableSubmit = true;
    console.log('videoType', this.videoType);
    if (typeof this.videoType.id !== 'undefined' && this.videoType.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.videoTypesDataService.updateVideoType(this.videoType).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('VIDEOTYPEUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPENOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.videoTypesDataService.addVideoType(this.videoType).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('VIDEOTYPECREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPENOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

}

@Component({
  selector: 'app-video-type-form',
  templateUrl: './video-type-form.component.html',
  styleUrls: ['./video-type-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogVideoTypeFormComponent extends VideoTypeFormComponent {
}

